import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { galleryItems } from '../../../mocks/gallery';

const rooms = ['All', 'Living Room', 'Bedroom', 'Dining Room', 'Home Office', 'Outdoor'];

export default function PhotoGallery() {
  const [activeRoom, setActiveRoom] = useState('All');
  const ref = useRef<HTMLDivElement>(null);

  const filtered = activeRoom === 'All' ? galleryItems : galleryItems.filter((g) => g.room === activeRoom);

  useEffect(() => {
    const o = new IntersectionObserver(([e]) => { if (e.isIntersecting) ref.current?.classList.add('visible'); }, { threshold: 0.1 });
    if (ref.current) o.observe(ref.current);
    return () => o.disconnect();
  }, []);

  return (
    <section className="py-28 bg-dark">
      <div ref={ref} className="fade-up max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-14 gap-6">
          <div>
            <div className="flex items-center gap-3 mb-4">
              <span className="w-8 h-px bg-gold" />
              <span className="text-gold text-xs font-semibold uppercase tracking-[0.25em]">Inspiration</span>
            </div>
            <h2 className="font-serif text-5xl font-bold text-white leading-tight">
              Real Homes,<br />
              <em className="text-gold not-italic">Real Results</em>
            </h2>
          </div>
          <Link
            to="/gallery"
            className="inline-flex items-center gap-2 border border-white/20 text-white px-6 py-3 rounded text-xs font-semibold uppercase tracking-wider hover:border-gold hover:text-gold transition-all cursor-pointer whitespace-nowrap self-start md:self-auto"
          >
            Full Gallery
            <i className="ri-arrow-right-line" />
          </Link>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-2 mb-10">
          {rooms.map((r) => (
            <button
              key={r}
              onClick={() => setActiveRoom(r)}
              className={`px-5 py-2 rounded-full text-xs font-semibold uppercase tracking-wider transition-all cursor-pointer whitespace-nowrap ${
                activeRoom === r
                  ? 'bg-gold text-white'
                  : 'bg-white/8 text-white/50 hover:bg-white/12 hover:text-white border border-white/10'
              }`}
            >
              {r}
            </button>
          ))}
        </div>

        {/* Masonry */}
        <div className="columns-1 sm:columns-2 lg:columns-3 xl:columns-4 gap-4 space-y-4">
          {filtered.map((item) => (
            <div key={item.id} className="break-inside-avoid group relative overflow-hidden rounded-xl cursor-pointer">
              <img
                src={item.image}
                alt={`${item.title} - modern furniture Las Vegas`}
                title={item.title}
                className="w-full object-cover object-top transition-transform duration-600 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-dark/80 via-dark/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex flex-col justify-end p-5">
                <span className="text-gold text-[10px] font-bold uppercase tracking-[0.2em] mb-1">{item.room}</span>
                <span className="text-white font-serif font-bold text-base">{item.title}</span>
                <span className="text-white/50 text-xs mt-1">{item.location}</span>
              </div>
              <div className="absolute top-3 left-3 bg-dark/60 backdrop-blur-sm text-white text-[10px] font-semibold uppercase tracking-wider px-3 py-1 rounded-full">
                {item.room}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
